import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Divider,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  OTHER_TAX_1_TOOLTIP,
  OTHER_TAX_2_TOOLTIP,
  OTHER_TAX_EXAMPLES,
  EMPTY_OTHER_TAXES,
  normalizeOtherTaxes,
} from '../../utils/billTaxes';
import { fetchBillOtherTaxes, saveBillOtherTaxes } from '../../services/adminSettingsService';
import { UPI_CONSENT_TEXT_EN, UPI_CONSENT_TEXT_TE } from '../../utils/upiPayeeConsent';
import Msg91OtpWidget from '../../components/Msg91OtpWidget';

const AdminControls = () => {
  const [upiId, setUpiId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialUpiId, setInitialUpiId] = useState('');
  const [upiScope, setUpiScope] = useState('global');
  const [otpPhoneMasked, setOtpPhoneMasked] = useState('');
  const [otpPhone, setOtpPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [msg91AccessToken, setMsg91AccessToken] = useState('');
  const [msg91RequestUserId, setMsg91RequestUserId] = useState('');
  const [upiAccountName, setUpiAccountName] = useState('');
  const [initialUpiAccountName, setInitialUpiAccountName] = useState('');
  const [upiConsent, setUpiConsent] = useState(true);
  const [otherTaxes, setOtherTaxes] = useState(EMPTY_OTHER_TAXES);
  const [initialOtherTaxes, setInitialOtherTaxes] = useState(EMPTY_OTHER_TAXES);
  const [savingTaxes, setSavingTaxes] = useState(false);
  const [taxError, setTaxError] = useState(null);

  // Fetch current UPI configuration on component mount
  useEffect(() => {
    fetchUpiConfiguration();
    loadBillOtherTaxes();
  }, []);

  const loadBillOtherTaxes = async () => {
    const data = await fetchBillOtherTaxes();
    const normalized = normalizeOtherTaxes(data);
    setOtherTaxes(normalized);
    setInitialOtherTaxes(normalized);
  };

  const fetchUpiConfiguration = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/settings/admin/upi');
      if (response.data.success) {
        const raw =
          response.data.editable_upi_id !== undefined && response.data.editable_upi_id !== null
            ? response.data.editable_upi_id
            : response.data.upi_id;
        const normalized = typeof raw === 'string' ? raw.trim() : '';
        setUpiId(normalized);
        setInitialUpiId(normalized);
        const accountName = String(response.data.upi_account_name || '').trim();
        setUpiAccountName(accountName);
        setInitialUpiAccountName(accountName);
        setUpiConsent(true);
        setUpiScope(
          response.data.scope === 'business'
            ? 'business'
            : response.data.scope === 'tenant'
              ? 'tenant'
              : 'global',
        );
        if (response.data.otp_phone_masked) {
          setOtpPhoneMasked(response.data.otp_phone_masked);
        }
      } else {
        // UPI not configured yet
        setUpiId('');
        setInitialUpiId('');
      }
    } catch (err) {
      // It's okay if UPI is not configured yet
      if (err.response?.status === 404) {
        setUpiId('');
        setInitialUpiId('');
      } else {
        console.error('Error fetching UPI:', err);
        setError('Failed to load UPI configuration. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Validate UPI format
  const validateUpiFormat = (upi) => {
    if (!upi || upi.trim() === '') {
      return { valid: false, message: 'UPI ID is required' };
    }

    const upiTrimmed = upi.trim();

    // UPI format: alphanumeric + dots/hyphens/underscores @ bank name
    // Example: john.doe@ybl or 9876543210@upi or name@okaxis
    const upiRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/;

    if (!upiRegex.test(upiTrimmed)) {
      return {
        valid: false,
        message: 'Invalid UPI format. Expected format: name@bank (e.g., john@ybl, 9876543210@upi)',
      };
    }

    return { valid: true, message: '' };
  };

  const handleUpiChange = (e) => {
    const value = e.target.value;
    setUpiId(value);
    setHasChanges(value !== initialUpiId || upiAccountName !== initialUpiAccountName);
    setError(null);
    setOtpSent(false);
    setOtpPhone('');
    setMsg91AccessToken('');
    setMsg91RequestUserId('');
  };

  const sendUpiOtp = async () => {
    const validation = validateUpiFormat(upiId);
    if (!validation.valid) {
      setError(validation.message);
      toast.error(validation.message);
      return false;
    }

    setSendingOtp(true);
    setError(null);
    setMsg91AccessToken('');
    setMsg91RequestUserId('');
    try {
      const response = await axios.post('/api/settings/admin/upi/send-otp');
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to prepare OTP');
      }
      const masked =
        response.data.otp_phone_masked ||
        response.data.data?.otp_phone_masked ||
        otpPhoneMasked;
      const phoneRaw =
        response.data.otp_phone || response.data.data?.otp_phone || '';
      const phone = String(phoneRaw).replace(/\D/g, '').slice(-10);
      if (phone.length !== 10) {
        throw new Error('Registered mobile missing from OTP prepare response.');
      }
      if (masked) setOtpPhoneMasked(masked);
      setOtpPhone(phone);
      setOtpSent(true);
      toast.success(`Verify OTP via MSG91 for ${masked || phone}`);
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to prepare OTP';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSave = async (msg91Override) => {
    const validation = validateUpiFormat(upiId);
    if (!validation.valid) {
      setError(validation.message);
      toast.error(validation.message);
      return;
    }
    const name = upiAccountName.trim();
    if (name.length < 2) {
      setError('Enter the account holder name shown in GPay / PhonePe for this UPI.');
      toast.error('Enter the account holder name shown in GPay / PhonePe for this UPI.');
      return;
    }
    if (!upiConsent) {
      setError('Please confirm you have checked this UPI ID and name.');
      toast.error('Please confirm you have checked this UPI ID and name.');
      return;
    }

    const upiChanged = upiId.trim() !== initialUpiId;
    const accessToken = msg91Override?.accessToken || msg91AccessToken;
    const requestUserId =
      msg91Override?.requestUserId ?? msg91RequestUserId;
    const verifiedPhone = msg91Override?.otpPhone || otpPhone;

    if (upiChanged && !otpSent) {
      await sendUpiOtp();
      return;
    }

    if (upiChanged && !accessToken) {
      setError('Verify the MSG91 OTP sent to your registered mobile first.');
      toast.error('Verify the MSG91 OTP sent to your registered mobile first.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await axios.post('/api/settings/admin/upi', {
        upi_id: upiId.trim(),
        upi_account_name: name,
        upi_consent: true,
        ...(upiChanged
          ? {
              msg91_access_token: accessToken,
              otp_phone: verifiedPhone,
              ...(requestUserId ? { msg91_request_user_id: requestUserId } : {}),
            }
          : {}),
      });

      if (response.data.success) {
        setInitialUpiId(upiId.trim());
        setInitialUpiAccountName(name);
        setHasChanges(false);
        setOtpSent(false);
        setOtpPhone('');
        setMsg91AccessToken('');
        setMsg91RequestUserId('');
        toast.success('✓ UPI ID saved successfully');
      } else {
        throw new Error(response.data.message || 'Failed to save UPI');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save UPI configuration';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error saving UPI:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const updateTaxSlot = (key, patch) => {
    setOtherTaxes((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }));
    setTaxError(null);
  };

  const taxesHaveChanges =
    JSON.stringify(otherTaxes) !== JSON.stringify(initialOtherTaxes);

  const handleSaveOtherTaxes = async () => {
    const p1 = Number(otherTaxes.tax1.percent) || 0;
    const p2 = Number(otherTaxes.tax2.percent) || 0;
    if (p1 < 0 || p1 > 100 || p2 < 0 || p2 > 100) {
      setTaxError('Other tax % must be between 0 and 100.');
      toast.error('Other tax % must be between 0 and 100.');
      return;
    }
    setSavingTaxes(true);
    setTaxError(null);
    try {
      await saveBillOtherTaxes(otherTaxes);
      setInitialOtherTaxes(otherTaxes);
      toast.success('Bill other taxes saved');
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to save other taxes';
      setTaxError(message);
      toast.error(message);
    } finally {
      setSavingTaxes(false);
    }
  };

  const renderOtherTaxSlot = (key, tooltip) => {
    const slot = otherTaxes[key];
    return (
      <Box sx={{ mb: 2, p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!slot.enabled}
                onChange={(e) => updateTaxSlot(key, { enabled: e.target.checked })}
                disabled={savingTaxes}
              />
            }
            label={slot.label || (key === 'tax1' ? 'Service Charge' : 'Municipal Tax')}
          />
          <Tooltip title={tooltip}>
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        </Box>
        {slot.enabled && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Label"
              value={slot.label}
              onChange={(e) => updateTaxSlot(key, { label: e.target.value })}
              disabled={savingTaxes}
              fullWidth
            />
            <TextField
              label="Percent"
              type="number"
              value={slot.percent}
              onChange={(e) => updateTaxSlot(key, { percent: e.target.value })}
              disabled={savingTaxes}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              sx={{ width: 140 }}
            />
          </Box>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const upiValidation = validateUpiFormat(upiId);
  const upiChanged = upiId.trim() !== initialUpiId;
  const isSaveDisabled =
    !upiId.trim() ||
    !upiValidation.valid ||
    upiAccountName.trim().length < 2 ||
    !upiConsent ||
    isSaving ||
    sendingOtp ||
    !hasChanges ||
    (upiChanged && otpSent && !msg91AccessToken);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <SettingsIcon sx={{ fontSize: 40, color: '#1976d2' }} />
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Admin Controls
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Billing UPI and workspace settings for your signed-in account
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                component="span"
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                1
              </Typography>
              Default UPI ID for Billing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
              This UPI is used for Smart Bill QR codes and payment links. Format: name@bank (e.g., john@ybl,
              9876543210@upi)
              {upiScope === 'business' && (
                <>
                  <br />
                  <strong>Your business workspace:</strong> the same UPI is used for your business admin and staff who
                  bill under this workspace.
                </>
              )}
              {upiScope === 'tenant' && (
                <>
                  <br />
                  <strong>Your franchise / center workspace:</strong> this UPI is stored only for your tenant account.
                </>
              )}
              {upiScope === 'global' && (
                <>
                  <br />
                  <strong>Your admin account:</strong> this UPI applies to your app-admin billing tools (not other
                  admins).
                </>
              )}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon sx={{ fontSize: 20 }} />
              {error}
            </Alert>
          )}

          {initialUpiId && !hasChanges && (
            <Alert severity="success" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 20 }} />
              Current UPI ID is configured and active
            </Alert>
          )}

          <TextField
            fullWidth
            label="Default UPI ID"
            placeholder="e.g., john@ybl or 9876543210@upi"
            value={upiId}
            onChange={handleUpiChange}
            disabled={isSaving || sendingOtp}
            error={!upiValidation.valid && upiId.trim() !== ''}
            helperText={!upiValidation.valid && upiId.trim() !== '' ? upiValidation.message : 'Format: name@bank'}
            variant="outlined"
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              },
            }}
            inputProps={{
              maxLength: 100,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                  ৳
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Account holder name (as in GPay / PhonePe)"
            placeholder="e.g. Ravi Kumar or Shop Name"
            value={upiAccountName}
            onChange={(e) => {
              setUpiAccountName(e.target.value);
              setHasChanges(upiId !== initialUpiId || e.target.value.trim() !== initialUpiAccountName);
              setError(null);
            }}
            disabled={isSaving || sendingOtp}
            helperText="This name is shown on the payment QR so you and your customer can confirm who receives the money."
            variant="outlined"
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 50 }}
          />

          <FormControlLabel
            sx={{ mb: 2, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={upiConsent}
                onChange={(e) => setUpiConsent(e.target.checked)}
                disabled={isSaving || sendingOtp}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  I have checked this UPI ID and name. My customers may pay this UPI.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {UPI_CONSENT_TEXT_EN}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {UPI_CONSENT_TEXT_TE}
                </Typography>
              </Box>
            }
          />

          {otpSent && otpPhone && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                OTP via MSG91 to registered mobile {otpPhoneMasked || otpPhone}
              </Typography>
              <Msg91OtpWidget
                phoneDigits10={otpPhone}
                disabled={isSaving}
                verifyButtonLabel="Verify OTP"
                onVerified={(token, meta) => {
                  const sessionId =
                    meta && String(meta.requestUserId || meta.reqId || '').trim();
                  setMsg91AccessToken(token);
                  setMsg91RequestUserId(sessionId || '');
                  setError(null);
                  toast.success('Mobile verified. Saving UPI…');
                  handleSave({
                    accessToken: token,
                    requestUserId: sessionId || '',
                    otpPhone,
                  });
                }}
                onError={(msg) => {
                  setMsg91AccessToken('');
                  setMsg91RequestUserId('');
                  setError(msg || 'MSG91 OTP failed');
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => handleSave()}
              disabled={isSaveDisabled}
              size="large"
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
                '&:disabled': {
                  backgroundColor: 'action.disabledBackground',
                },
              }}
            >
              {sendingOtp
                ? 'Sending OTP...'
                : isSaving
                  ? 'Saving...'
                  : upiChanged && otpSent
                    ? 'Verify OTP & Save UPI'
                    : upiChanged
                      ? 'Send OTP to save'
                      : 'Save UPI'}
            </Button>
            {otpSent && (
              <Button
                variant="outlined"
                onClick={sendUpiOtp}
                disabled={sendingOtp || isSaving}
              >
                {sendingOtp ? 'Sending...' : 'Resend OTP'}
              </Button>
            )}

            {hasChanges && (
              <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
                ● You have unsaved changes
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              ℹ️ Important Information
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div" sx={{ lineHeight: 1.8 }}>
              <ul style={{ marginLeft: '0px', paddingLeft: '20px' }}>
                <li>The UPI ID you configure here will be used for:</li>
                <ul style={{ marginTop: '4px' }}>
                  <li>Generating QR codes on bill pages</li>
                  <li>WhatsApp messages for payments</li>
                  <li>All payment-related communications</li>
                </ul>
                <li style={{ marginTop: '8px' }}>This is a mandatory field - billing will not function without a valid UPI</li>
                <li style={{ marginTop: '8px' }}>
                  Changing UPI requires a 6-digit OTP sent to the Mitra registered mobile
                  {otpPhoneMasked ? ` (${otpPhoneMasked})` : ''}
                </li>
                <li style={{ marginTop: '8px' }}>
                  Updates apply to this signed-in workspace only (other businesses and other app admins keep their own
                  UPI)
                </li>
              </ul>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Bill Other Taxes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set default extra charges for every new product or service bill. Leave 0% if you do not use them.
            <br />
            {OTHER_TAX_EXAMPLES}
          </Typography>
          {taxError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {taxError}
            </Alert>
          )}
          {renderOtherTaxSlot('tax1', OTHER_TAX_1_TOOLTIP)}
          {renderOtherTaxSlot('tax2', OTHER_TAX_2_TOOLTIP)}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveOtherTaxes}
            disabled={savingTaxes || !taxesHaveChanges}
          >
            {savingTaxes ? 'Saving...' : 'Save other taxes'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminControls;
